import { useEffect, useMemo, useState } from 'react'
import { Box, Chip, Paper, Skeleton, Stack, Tooltip, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import { formatTime, toSpan } from 'components/Schedule/layout'
import { MIN_CARD_HEIGHT } from '../cardHeight'

const TIME_WIDTH = 64
const FREE_MIN = 15

const isBreak = entry => entry.type && entry.type !== 'course-session'

const nowMinutes = () => {
  const now = new Date()
  return now.getHours() * 60 + now.getMinutes()
}

const useMinute = () => {
  const [minutes, setMinutes] = useState(nowMinutes)

  useEffect(() => {
    const timer = setInterval(() => setMinutes(nowMinutes()), 60_000)
    return () => clearInterval(timer)
  }, [])

  return minutes
}

const Teachers = ({ teachers }) => {
  if (!teachers?.length) return null

  const [first, ...rest] = teachers

  return (
    <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0, display: { xs: 'none', md: 'flex' } }}>
      <Chip size="small" variant="outlined" label={first} sx={{ maxWidth: 140 }} />
      {rest.length > 0 && (
        <Tooltip title={teachers.join(', ')}>
          <Chip size="small" variant="outlined" label={`+${rest.length}`} />
        </Tooltip>
      )}
    </Stack>
  )
}

const Hero = ({ entry, label, detail }) => (
  <Box
    sx={theme => ({
      p: 1.75,
      mb: 1.5,
      borderRadius: 2,
      border: '1px solid',
      borderColor: alpha(theme.palette.primary.main, 0.35),
      bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.18 : 0.07),
    })}
  >
    <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 0.5 }}>
      <Typography variant="overline" color="primary.main" fontWeight={700} sx={{ letterSpacing: 1.2 }}>
        {label}
      </Typography>
      {detail && (
        <Typography variant="caption" color="text.secondary">
          {detail}
        </Typography>
      )}
    </Stack>

    <Stack direction="row" alignItems="center" spacing={1}>
      <Typography variant="h6" fontWeight={700} noWrap sx={{ flex: 1, minWidth: 0 }}>
        {entry.label}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
        {formatTime(entry.start)} – {formatTime(entry.end)}
      </Typography>
    </Stack>

    <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.75 }}>
      {entry.room && <Chip size="small" color="primary" variant="outlined" label={entry.room} />}
      <Teachers teachers={entry.teachers} />
    </Stack>
  </Box>
)

const Row = ({ entry, state }) => (
  <Stack
    direction="row"
    alignItems="center"
    spacing={1.5}
    sx={theme => ({
      px: 1,
      py: 0.875,
      borderRadius: 1,
      opacity: state === 'done' ? 0.45 : 1,
      bgcolor: state === 'current' ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
      borderLeft: '3px solid',
      borderLeftColor:
        state === 'current' ? 'primary.main' : isBreak(entry) ? 'transparent' : alpha(theme.palette.divider, 0.9),
    })}
  >
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ width: TIME_WIDTH, flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}
    >
      {formatTime(entry.start)}
    </Typography>

    <Typography
      variant="body2"
      fontWeight={state === 'current' ? 700 : 500}
      noWrap
      sx={{ flex: 1, minWidth: 0 }}
    >
      {entry.label}
    </Typography>

    {entry.room && <Chip size="small" variant="outlined" label={entry.room} sx={{ flexShrink: 0 }} />}
    <Teachers teachers={entry.teachers} />
  </Stack>
)

const FreeRow = ({ minutes }) => {
  const { t } = useTranslation()

  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 1, py: 0.25 }}>
      <Box sx={{ width: TIME_WIDTH, flexShrink: 0 }} />
      <Box sx={{ flex: 1, borderTop: '1px dashed', borderColor: 'divider' }} />
      <Typography variant="caption" color="text.disabled" sx={{ flexShrink: 0 }}>
        {t('No Class · {{count}} min', { count: minutes })}
      </Typography>
      <Box sx={{ flex: 1, borderTop: '1px dashed', borderColor: 'divider' }} />
    </Stack>
  )
}

export default function DaySchedule({
  title,
  items = [],
  isReady = true,
  emptyMessage = 'No classes scheduled today.',
  minHeight = MIN_CARD_HEIGHT,
}) {
  const { t } = useTranslation()
  const minutes = useMinute()

  const entries = useMemo(
    () =>
      (items ?? [])
        .map(item => ({ item, span: toSpan(item) }))
        .filter(entry => entry.span)
        .map(entry => ({ ...entry.item, start: entry.span.start, end: entry.span.end }))
        .sort((a, b) => a.start - b.start),
    [items],
  )

  const current = entries.find(entry => minutes >= entry.start && minutes < entry.end)
  const next = entries.find(entry => entry.start > minutes)

  const stateOf = entry => {
    if (entry === current) return 'current'
    if (entry.end <= minutes) return 'done'

    return 'upcoming'
  }

  const hero = current
    ? { entry: current, label: t('Now'), detail: t('ends in {{count}} min', { count: current.end - minutes }) }
    : next
      ? { entry: next, label: t('Up next'), detail: t('starts in {{count}} min', { count: next.start - minutes }) }
      : null

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        minHeight,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Stack direction="row" alignItems="baseline" justifyContent="space-between" spacing={1} sx={{ mb: 1.5 }}>
        <Typography variant="subtitle1" fontWeight={700} noWrap>
          {title}
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap>
          {dayjs().format('dddd, MMM D')}
        </Typography>
      </Stack>

      {!isReady ? (
        <Stack spacing={1}>
          <Skeleton variant="rounded" height={88} />
          {[0, 1, 2, 3].map(index => (
            <Skeleton key={index} variant="rounded" height={36} />
          ))}
        </Stack>
      ) : !entries.length ? (
        <Typography variant="body2" color="text.secondary">
          {t(emptyMessage)}
        </Typography>
      ) : (
        <Box sx={{ overflow: 'auto', flex: 1 }}>
          {hero ? (
            <Hero {...hero} />
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {t('All classes finished for today.')}
            </Typography>
          )}

          <Stack spacing={0.25}>
            {entries.map((entry, index) => {
              const previous = entries[index - 1]
              const gap = previous ? entry.start - previous.end : 0

              return (
                <Box key={`${entry.label}-${entry.start}-${index}`}>
                  {gap >= FREE_MIN && <FreeRow minutes={gap} />}
                  <Row entry={entry} state={stateOf(entry)} />
                </Box>
              )
            })}
          </Stack>
        </Box>
      )}
    </Paper>
  )
}
